import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import IconButton from '@material-ui/core/IconButton';
import NavigateBeforeRoundedIcon from '@material-ui/icons/NavigateBeforeRounded';
import NavigateNextRoundedIcon from '@material-ui/icons/NavigateNextRounded';
import Alert from '@material-ui/lab/Alert';
import accountsStore from 'store/accountsStore';
import { AxiosStatus } from 'api/axiosConfig';
import Button, { Sizes } from 'ui/Button/Button';
import Loader from 'ui/Loader/Loader';
import auth from 'utils/auth';
import { formatAMPM } from 'utils/formatters';
import AccountCard from './AccountCard';

import styles from './YourAccounts.module.css';

const setActiveAccount = (accountNumber: string) => (
  () => accountsStore.setActiveAccountNumber(accountNumber)
);

const getVisibleCardsCount = (fullHdScreen, xlScreen, smallScreen) => {
  if (fullHdScreen) {
    return 4;
  }
  if (xlScreen || smallScreen) {
    return 3;
  }
  return 2; // md or lg screen
};

const YourAccounts = () => {
  const theme = useTheme();
  const fullHdScreen = useMediaQuery(theme.breakpoints.up('fullhd'));
  const xlScreen = useMediaQuery(theme.breakpoints.only('xl'));
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    accountsStore.fetchAccounts().catch(console.error);
  }, []);

  const { accounts, activeAccountNumber, fetchingStatus } = accountsStore;
  // Sort accounts to have the primary account first
  // const sortedAccounts = [...accounts].sort((a, b) => (b.primaryAccount ? 1 : 0) - (a.primaryAccount ? 1 : 0));

  const [offset, setOffset] = useState(0);

  const cardsCount = accounts.length;
  const visibleCardsCount = getVisibleCardsCount(fullHdScreen, xlScreen, smallScreen);
  const offsetStep = 100 / cardsCount;
  const offsetLimit = (cardsCount - visibleCardsCount) * offsetStep;

  if (offsetLimit < offset && visibleCardsCount <= cardsCount) {
    // after up-resizing on scrolled cards
    setOffset(offsetLimit);
  }

  const getParsedDate: (date: string) => string = useCallback(
    // eslint-disable-next-line newline-per-chained-call
    (date) => {
      const t = date.split(/[- :]/);

      // Apply each element to the Date function
      const d = new Date(+t[0], +t[1] - 1, +t[2], +t[3], +t[4], +t[5]);
      return new Date(d).toJSON()?.slice(0, 10)?.split('-').reverse().join('-') || '';
    },
    [],
  );

  const customerName = auth.data?.companyName?.toLowerCase();
  const { data } = auth;
  const roles = data?.roles;
  const roleNames = roles ? roles.map((item) => item.name) : [];
  let customerRole = '';

  if (roleNames.length > 0) {
    customerRole = roleNames.join(', ');

    if (roleNames.length === 2) {
      // If there are exactly two roles, add "and" between them
      customerRole = roleNames.join(' and ');
    }
  }

  const loginTime = auth.data?.lastLoginTime;

  const getTime = (date: string): Date => {
    const t = date.split(/[- :]/);

    // Apply each element to the Date function
    const d = new Date(+t[0], +t[1] - 1, +t[2], +t[3], +t[4], +t[5]);
    return new Date(d);
  };

  const toCorrectDateFormat = (date: Date): string => `${date.toISOString().slice(0, 10)} ${date.toLocaleTimeString()}`;

  const isScrollForwardDisabled = offsetLimit <= offset;
  const isScrollBackDisabled = offset === 0;
  const scrollAccountsForward = () => setOffset(((prev) => prev + offsetStep));
  const scrollAccountsBack = () => setOffset(((prev) => prev - offsetStep));

  const stripeStyles = smallScreen
    ? undefined
    : {
      width: `${(100 / visibleCardsCount) * cardsCount}%`,
      transform: `translate(-${offset}%)`,
    };

  if (fetchingStatus === AxiosStatus.FAILURE) {
    const actionBtn = (
      <Button size={Sizes.small} onClick={() => accountsStore.fetchAccounts()}>
        TRY AGAIN
      </Button>
    );
    return (
      <Alert severity="error" action={actionBtn} style={{ maxWidth: 480, margin: 'auto' }}>
        We could not get your accounts!
      </Alert>
    );
  }

  if (fetchingStatus === AxiosStatus.PENDING) {
    return (
      <div className={styles.spinnerWrapper}>
        <Loader />
      </div>
    );
  }

  return (
    <>
      <div className={styles.headerWrapper}>
        <h2 className={styles.title}>{customerName || 'Your Accounts'}
          <span className={styles.role}>{` (${customerRole})`}</span>
        </h2>
        <span className={styles.loginTime}>
          Last login {getParsedDate(loginTime || toCorrectDateFormat(new Date()))} {formatAMPM(
          getTime(loginTime || toCorrectDateFormat(new Date())),
        )}
        </span>
      </div>
      <section className={styles.flexContainer}>
        <div className={styles.accounts}>
          <div className={styles.accountsStripe} style={stripeStyles}>
            {accounts.map((account) => {
              const { accountNumber } = account;
              return (
                <div key={accountNumber} className={styles.cardSection}>
                  <AccountCard
                    account={account}
                    isActive={accountNumber === activeAccountNumber}
                    onClick={setActiveAccount(accountNumber)}
                  />
                </div>
              );
            })}
          </div>
        </div>
        {!smallScreen && (
          <div className={styles.buttonPanel}>
            <IconButton
              aria-label="forward"
              color="inherit"
              className={styles.button}
              onClick={scrollAccountsForward}
              disabled={isScrollForwardDisabled}
            >
              <NavigateNextRoundedIcon fontSize="small" />
            </IconButton>
            <IconButton
              aria-label="back"
              color="inherit"
              className={styles.button}
              onClick={scrollAccountsBack}
              disabled={isScrollBackDisabled}
            >
              <NavigateBeforeRoundedIcon fontSize="small" />
            </IconButton>
          </div>
        )}
      </section>
    </>
  );
};

export default observer(YourAccounts);
